if (!customElements.get("product-form")) {
  const MONEY_FORMAT_REGEX = /\{\{\s*(\w+)\s*\}\}/;

  const formatWithDelimiters = (
    number,
    precision = 2,
    thousands = ",",
    decimal = "."
  ) => {
    if (Number.isNaN(number) || number == null) {
      return "0";
    }

    const fixedNumber = (number / 1).toFixed(precision);
    const parts = fixedNumber.split(".");
    const dollarsAmount = parts[0].replace(
      /(\d)(?=(\d\d\d)+(?!\d))/g,
      `$1${thousands}`
    );
    const centsAmount = parts[1] ? `${decimal}${parts[1]}` : "";

    return `${dollarsAmount}${centsAmount}`;
  };

  const formatMoney = (cents, format) => {
    const formatString = format || "${{amount}}";
    const normalizedCents = Number(cents);
    const amount = Number.isFinite(normalizedCents) ? normalizedCents : 0;
    const placeholder = formatString.match(MONEY_FORMAT_REGEX)?.[1] || "amount";

    let formattedValue = "";
    switch (placeholder) {
      case "amount":
        formattedValue = formatWithDelimiters(amount / 100, 2, ",", ".");
        break;
      case "amount_no_decimals":
        formattedValue = formatWithDelimiters(amount / 100, 0, ",", ".");
        break;
      case "amount_with_comma_separator":
        formattedValue = formatWithDelimiters(amount / 100, 2, ".", ",");
        break;
      case "amount_no_decimals_with_comma_separator":
        formattedValue = formatWithDelimiters(amount / 100, 0, ".", ",");
        break;
      case "amount_with_apostrophe_separator":
        formattedValue = formatWithDelimiters(amount / 100, 2, "'", ".");
        break;
      default:
        formattedValue = formatWithDelimiters(amount / 100, 2, ",", ".");
        break;
    }

    return formatString.replace(MONEY_FORMAT_REGEX, formattedValue);
  };

  customElements.define(
    "product-form",
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector("form");
        this.variantIdInput.disabled = false;
        this.form.addEventListener("submit", this.onSubmitHandler.bind(this));
        this.cart =
          document.querySelector("cart-notification") ||
          document.querySelector("cart-drawer");
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector("span");
        this.submitButtonPrice =
          this.submitButton.querySelector("[data-atc-total]");
        this.submitButtonSeparator = this.submitButton.querySelector(
          ".atc-button__separator"
        );
        this.submitButtonLabel =
          this.submitButton.querySelector(".atc-button__label");

        if (document.querySelector("cart-drawer"))
          this.submitButton.setAttribute("aria-haspopup", "dialog");

        this.hideErrors = this.dataset.hideErrors === "true";
        this.disableInlineAtcPrice =
          this.dataset.disableInlineAtcPrice === "true";

        this.quantityInput = this.getQuantityInput();
        this.quantityInput?.addEventListener(
          "change",
          this.onQuantityInputChange
        );
        this.variantInput = this.variantIdInput;
        this.variantInput?.addEventListener(
          "change",
          this.onVariantInputChange
        );

        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this.onVariantChange.bind(this)
        );

        this.updateAddToCartTotal();
      }

      disconnectedCallback() {
        this.quantityInput?.removeEventListener(
          "change",
          this.onQuantityInputChange
        );
        this.variantInput?.removeEventListener(
          "change",
          this.onVariantInputChange
        );
        this.variantChangeUnsubscriber?.();
      }

      onQuantityInputChange = () => {
        this.updateAddToCartTotal();
      };

      onVariantInputChange = () => {
        this.updateAddToCartTotal();
      };

      onVariantChange = ({ data }) => {
        if (!data?.sectionId || !data?.variant) return;
        if (this.dataset.sectionId !== data.sectionId) return;

        this.submitButton.dataset.atcUnitPrice = data.variant.price;
        this.updateAddToCartTotal();
      };

      getQuantityInput() {
        const quantityInputId = this.submitButton?.dataset.atcQuantityInputId;
        if (quantityInputId) {
          return document.getElementById(quantityInputId);
        }

        return this.form.querySelector(".quantity__input");
      }

      getCurrentQuantity() {
        const fallbackQuantity = Number(
          this.submitButton?.dataset.atcDefaultQuantity || 1
        );
        const inputValue = this.quantityInput?.value;
        if (inputValue == null || inputValue === "") {
          return Number.isFinite(fallbackQuantity) ? fallbackQuantity : 1;
        }

        const parsedValue = Number(String(inputValue).replace(",", "."));
        if (!Number.isFinite(parsedValue) || parsedValue <= 0) {
          return Number.isFinite(fallbackQuantity) ? fallbackQuantity : 1;
        }

        return parsedValue;
      }

      clampQuantityInputToBounds() {
        if (!this.quantityInput) return;

        const current = Number(
          String(this.quantityInput.value).replace(",", ".")
        );
        const min = this.quantityInput.min
          ? Number(this.quantityInput.min)
          : null;
        const max = this.quantityInput.max
          ? Number(this.quantityInput.max)
          : null;

        let nextValue = Number.isFinite(current)
          ? current
          : Number.isFinite(min)
            ? min
            : 1;

        if (Number.isFinite(min)) nextValue = Math.max(nextValue, min);
        if (Number.isFinite(max)) nextValue = Math.min(nextValue, max);

        this.quantityInput.value = Number.isInteger(nextValue)
          ? `${nextValue}`
          : `${nextValue}`;
      }

      syncDecimalDisplayBeforeSubmit() {
        const decimalDisplayInput = this.closest("product-info")?.querySelector(
          "decimal-quantity-input input[name='quantity_display']"
        );
        if (!decimalDisplayInput) return;
        decimalDisplayInput.dispatchEvent(
          new Event("change", { bubbles: true })
        );
      }

      getMoneyFormatForButton() {
        const currencyFormats = window.currencyFormats || {};
        const useCurrencyCode = Boolean(currencyFormats.currencyCodeEnabled);

        return useCurrencyCode
          ? currencyFormats.moneyWithCurrencyFormat
          : currencyFormats.moneyFormat;
      }

      updateAddToCartTotal() {
        if (!this.submitButtonPrice) return;

        const unitPrice = Number(this.submitButton?.dataset?.atcUnitPrice);
        if (!Number.isFinite(unitPrice)) return;

        const quantity = this.getCurrentQuantity();
        const total = Math.round(unitPrice * quantity);
        const moneyFormat = this.getMoneyFormatForButton();

        this.submitButtonPrice.textContent = formatMoney(total, moneyFormat);
      }

      ensureInlineAtcNodes() {
        if (this.submitButtonLabel && this.submitButtonPrice) return true;
        if (
          !this.submitButtonText ||
          !this.submitButton?.dataset?.atcUnitPrice
        ) {
          return false;
        }

        this.submitButtonText.textContent = "";

        this.submitButtonLabel = document.createElement("span");
        this.submitButtonLabel.className = "atc-button__label";

        this.submitButtonSeparator = document.createElement("span");
        this.submitButtonSeparator.className = "atc-button__separator";
        this.submitButtonSeparator.setAttribute("aria-hidden", "true");
        this.submitButtonSeparator.textContent = " • ";

        this.submitButtonPrice = document.createElement("span");
        this.submitButtonPrice.className = "atc-button__amount";
        this.submitButtonPrice.dataset.atcTotal = "";

        this.submitButtonText.appendChild(this.submitButtonLabel);
        this.submitButtonText.appendChild(this.submitButtonSeparator);
        this.submitButtonText.appendChild(this.submitButtonPrice);

        return true;
      }

      showInlineAtcPrice(labelText) {
        if (!this.ensureInlineAtcNodes()) return false;

        this.submitButtonLabel.textContent = labelText;
        this.submitButtonSeparator?.classList.remove("hidden");
        this.submitButtonPrice?.classList.remove("hidden");
        this.updateAddToCartTotal();
        return true;
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute("aria-disabled") === "true") return;

        this.syncDecimalDisplayBeforeSubmit();
        this.clampQuantityInputToBounds();
        this.handleErrorMessage();

        this.submitButton.setAttribute("aria-disabled", true);
        this.submitButton.classList.add("loading");
        this.querySelector(".loading__spinner").classList.remove("hidden");

        const config = fetchConfig("javascript");
        config.headers["X-Requested-With"] = "XMLHttpRequest";
        delete config.headers["Content-Type"];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            "sections",
            this.cart.getSectionsToRender().map(section => section.id)
          );
          formData.append("sections_url", window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then(response => response.json())
          .then(response => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: "product-form",
                productVariantId: formData.get("id"),
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage =
                this.submitButton.querySelector(".sold-out-message");
              if (!soldOutMessage) return;
              this.submitButton.setAttribute("aria-disabled", true);
              this.submitButtonText.classList.add("hidden");
              soldOutMessage.classList.remove("hidden");
              this.error = true;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker(
              "add:wait-for-subscribers"
            );
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: "product-form",
                productVariantId: formData.get("id"),
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker(
                  "add:wait-for-subscribers",
                  startMarker
                );
              });
            this.error = false;
            const quickAddModal = this.closest("quick-add-modal");
            if (quickAddModal) {
              document.body.addEventListener(
                "modalClosed",
                () => {
                  setTimeout(() => {
                    CartPerformance.measure(
                      "add:paint-updated-sections",
                      () => {
                        this.cart.renderContents(response);
                      }
                    );
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch(e => {
            console.error(e);
          })
          .finally(() => {
            this.submitButton.classList.remove("loading");
            if (this.cart && this.cart.classList.contains("is-empty"))
              this.cart.classList.remove("is-empty");
            if (!this.error) this.submitButton.removeAttribute("aria-disabled");
            this.querySelector(".loading__spinner").classList.add("hidden");

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper ||
          this.querySelector(".product-form__error-message-wrapper");
        if (!this.errorMessageWrapper) return;
        this.errorMessage =
          this.errorMessage ||
          this.errorMessageWrapper.querySelector(
            ".product-form__error-message"
          );

        this.errorMessageWrapper.toggleAttribute("hidden", !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute("disabled", "disabled");
          if (text) {
            if (this.submitButtonLabel) {
              this.submitButtonLabel.textContent = text;
              this.submitButtonSeparator?.classList.add("hidden");
              this.submitButtonPrice?.classList.add("hidden");
            } else {
              this.submitButtonText.textContent = text;
            }
          }
        } else {
          this.submitButton.removeAttribute("disabled");
          if (this.disableInlineAtcPrice) {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
            return;
          }
          if (!this.showInlineAtcPrice(window.variantStrings.addToCart)) {
            this.submitButtonText.textContent = window.variantStrings.addToCart;
          }
        }
      }

      get variantIdInput() {
        return this.form.querySelector("[name=id]");
      }
    }
  );
}
